import { useState } from 'react';
import Permission from '../../components/Permission';
import { DATE_FORMATER, PERMISSIONS } from '../../constants';

import { Alert, AlertTitle, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { formatCpf, getChainId } from '../../lib';
import { ChipField, Datagrid, DateField, FunctionField, List, NumberField, ReferenceArrayField, ReferenceField, RichTextField, SearchInput, SelectField, SelectInput, Show, SimpleShowLayout, SingleFieldList, Tab, TabbedShowLayout, TextField, TextFieldProps } from 'ra-ui-materialui';
import { useGetRecordId, useListContext, useRecordContext } from 'react-admin';
import { CustomerDispatchMessageStatus, DispatchMessageOrigin, DispatchMessageStatus } from '../../models';
import { CPFField } from '../../components/Fields';
import { Tooltip } from '../../components';
import { onlyNumbers } from '../../utils';

const CARDINALITY_TEXT_INFO = 'A cardinalidade do envio será definida no momento do envio';

const richTextImageSx: SxProps<Theme> = {
    "& img": {
        maxWidth: 300,
        maxHeight: 300
    }
};

const titleSx: SxProps<Theme> = {
    margin: '10px 0 10px 0',
    textAlign: 'center',
    fontWeight: 'bold'
};

const CardinalityField: React.FC<TextFieldProps> = ({ source, textAlign }) => {
    const record = useRecordContext();
    return (
        record && source && record[source] !== null ?
            <NumberField record={record} source={source} />
            :
            <Tooltip
                title={CARDINALITY_TEXT_INFO}
                textAlign={textAlign}
                //@ts-ignore
                placement="bottom">A definir</Tooltip>
    );
}

const CardinalityInfo = ({ children }) => {
    const record = useRecordContext();

    return (
        (record && !record.cardinality) ?
            <Alert
                severity="info"
                sx={{ marginTop: 2, marginBottom: 2 }}
            >
                <AlertTitle>Aviso</AlertTitle>
                {CARDINALITY_TEXT_INFO}
            </Alert>
            :
            <>
                {children}
            </>
    );
}

const SegmentationField = () => {
    const record = useRecordContext();
    if (!record) return null;

    // Check which segmentation type exists based on origin
    if (record.origin === DispatchMessageOrigin.discountMarketingCampaign) {
        return (
            <>
                <Typography variant="caption" color="textSecondary">Grupo de Clientes</Typography>
                <ReferenceField source="messageId" reference="marketing-discounts" link={false} emptyText="--">
                    <ReferenceField source="discountId" reference="discounts" link={false}>
                        <ReferenceArrayField reference="chains/chainId/customer-groups" source="customerGroupIds">
                            <SingleFieldList linkType={false}>
                                <ChipField source="name" />
                            </SingleFieldList>
                        </ReferenceArrayField>
                    </ReferenceField>
                </ReferenceField>
            </>
        );
    }

    if (record.origin === DispatchMessageOrigin.unifiedSend) {
        return (
            <ReferenceField source="messageId" reference="chains/chainId/unified-send" link={false}>
                <UnifiedSendSegmentationField />
            </ReferenceField>
        );
    }

    return <Typography variant="body2" color="textSecondary">Todos os clientes</Typography>;
};

const UnifiedSendSegmentationField = () => {
    const record = useRecordContext();
    if (!record) return null;

    if (record.customerGroupId) {
        return (
            <>
                <Typography variant="caption" color="textSecondary">Grupo de Clientes</Typography>
                <ReferenceField source="customerGroupId" reference="chains/chainId/customer-groups" link={false}>
                    <ChipField source="name" />
                </ReferenceField>
            </>
        );
    }

    if (record.stationGroupId) {
        return (
            <>
                <Typography variant="caption" color="textSecondary">Grupo de Postos</Typography>
                <ReferenceField source="stationGroupId" reference="chains/chainId/station-groups" link={false}>
                    <ChipField source="name" />
                </ReferenceField>
            </>
        );
    }

    if (record.clientCategoryId) {
        return (
            <>
                <Typography variant="caption" color="textSecondary">Categoria de Clientes</Typography>
                <ReferenceField source="clientCategoryId" reference="client-categories" link={false}>
                    <ChipField source="label" />
                </ReferenceField>
            </>
        );
    }

    if (record.customerTagId) {
        return (
            <>
                <Typography variant="caption" color="textSecondary">Etiqueta (clientes)</Typography>
                <ReferenceField source="customerTagId" reference="chains/chainId/tag" link={false}>
                    <ChipField source="name" />
                </ReferenceField>
            </>
        );
    }

    if (record.stationTagId) {
        return (
            <>
                <Typography variant="caption" color="textSecondary">Etiqueta (postos)</Typography>
                <ReferenceField source="stationTagId" reference="chains/chainId/tag" link={false}>
                    <ChipField source="name" />
                </ReferenceField>
            </>
        );
    }

    return <Typography variant="body2" color="textSecondary">Todos os clientes</Typography>;
};

const CustomerDispatchMessageList = () => {

    const props = useListContext();
    let data = [];
    let summaryData = [];
    let isSummarized = false;

    if (props.data) {
        const listData = props.data[0];

        if (listData) {
            data = listData.data;
            summaryData = listData.summaryData;
            isSummarized = summaryData?.[0]?.is_summarized === true;
        }
    }

    return (
        <>
            <Typography sx={titleSx}>Totais</Typography>
            <Datagrid data={summaryData} bulkActionButtons={false} total={1}>
                <NumberField source="total_dispatched" label="Enviados" textAlign="center" emptyText="0" sortable={false} />
                {/* "Recebidas" oculto: subconta demais (iOS não roda o handler em quit + /log sem retry).
                    Enviados = o que o FCM aceitou; entrega real só via delivery receipts do FCM. */}
                <NumberField source="total_failed_dispatches" label="Erro ao enviar" textAlign="center" emptyText="0" sortable={false} />
                <NumberField source="total_is_read" label="Lidas" textAlign="center" emptyText="0" sortable={false} />
                <NumberField source="total_is_clicked" label="Clicadas" textAlign="center" emptyText="0" sortable={false} />
            </Datagrid>

            {isSummarized ? (
                <Alert severity="info" sx={{ marginTop: 3 }}>
                    <AlertTitle>Dados arquivados</AlertTitle>
                    Os dados detalhados de usuários para este envio foram arquivados.
                    As estatísticas resumidas acima refletem o estado final do envio.
                </Alert>
            ) : (
                <>
                    <Typography sx={{ ...titleSx, marginTop: '20px' }}>Usuários</Typography>
                    <Datagrid data={data} bulkActionButtons={false} total={1}>
                        <ReferenceField source="customerId" reference="customers" label="Nome" link="show" sortable={false}>
                            <TextField source="name" />
                        </ReferenceField>
                        <ReferenceField source="customerId" reference="customers" label="CPF" link="show" sortable={false}>
                            <CPFField source="cpf" />
                        </ReferenceField>
                        <DateField
                            source="readAt"
                            label="Lida em"
                            //@ts-ignore
                            options={DATE_FORMATER}
                            emptyText="--"
                            sortable={false}
                            showTime
                        />
                        <DateField
                            source="clickedAt"
                            label="Clicado em"
                            //@ts-ignore
                            options={DATE_FORMATER}
                            emptyText="--"
                            sortable={false}
                            showTime
                        />
                    </Datagrid>
                </>
            )}
        </>
    );
}

const DispatchMessageShow = (props) => {

    const [chainId] = useState(getChainId());
    if (!chainId) { return <Typography>Selecione uma Rede</Typography>; }

    const recordId = useGetRecordId();

    const customerDispatchMessageFilters = [
        <SearchInput
            source="search"
            placeholder="CPF"
            format={value => formatCpf(value || '')}
            parse={value => onlyNumbers(value)}
            alwaysOn
        />,
        <SelectInput
            source="readStatus"
            label="Status de leitura"
            choices={[
                { id: 'read', name: "Lido" },
                { id: 'not-read', name: "Não lido" },
            ]}
            emptyText="Todos"
            alwaysOn
        />,
        <SelectInput
            source="clickStatus"
            label="Status de clique"
            choices={[
                { id: 'clicked', name: "Clicado" },
                { id: 'not-clicked', name: "Não clicado" },
            ]}
            emptyText="Todos"
            alwaysOn
        />,
    ];

    return (
        <Permission permission={PERMISSIONS.ATTENDANT_TARGET}>
            <Show title='Envio' {...props}>
                <TabbedShowLayout>
                    <Tab label="Geral">
                        <Grid container width={{ xs: '100%', xl: 1200 }} spacing={2} direction="row" gap={4}>
                            <Grid size={{ xs: 12, md: 8, lg: 6 }}>
                                <Card>
                                    <CardContent>
                                        <SimpleShowLayout sx={{ padding: 0 }}>
                                            <TextField source="message.title" label="Título" />
                                            <TextField source="message.pushMessage" label="Mensagem push" />
                                        </SimpleShowLayout>
                                        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
                                        <SimpleShowLayout sx={{ padding: 0, marginTop: 0 }}>
                                            <RichTextField sx={richTextImageSx} source="message.body" label="Corpo da Mensagem" />
                                        </SimpleShowLayout>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 3, lg: 5 }}>
                                <SimpleShowLayout sx={{ padding: 0 }}>
                                    <FunctionField label='Tipo' render={record => record?.type === 'sms' ? 'SMS' : 'Notificação pelo aplicativo'} />
                                    <SelectField
                                        source="gender"
                                        label="Sexo"
                                        choices={[
                                            { id: 'F', name: 'Feminino' },
                                            { id: 'M', name: 'Masculino' },
                                        ]}
                                        emptyText="Todos"
                                    />
                                    <CardinalityField source="cardinality" label="Cardinalidade" />
                                    <DateField source="createdAt" label="Data de criação" showTime locales="pt-BR" />
                                    <DateField source="sendAt" label="Data de envio" showTime locales="pt-BR" />
                                    <FunctionField label='Status' render={record => DispatchMessageStatus.translateType(record?.status)} />
                                    <SegmentationField />
                                    <FunctionField source="origin" label="Tipo" render={record => DispatchMessageOrigin.translateOrigin(record ? record.origin : null)} />
                                </SimpleShowLayout>
                            </Grid>
                        </Grid>
                    </Tab>
                    <Tab label="Clientes">
                        <CardinalityInfo>
                            <List
                                resource={`chains/chainId/dispatch-messages/${recordId}/customers`}
                                title=" "
                                filters={customerDispatchMessageFilters}
                                exporter={false}
                                disableSyncWithLocation
                            >
                                <CustomerDispatchMessageList />
                            </List>
                        </CardinalityInfo>
                    </Tab>
                </TabbedShowLayout>
            </Show>
        </Permission>
    );
};

export default DispatchMessageShow;
